import { useMemo } from "react";
import type { ReactNode } from "react";
import { Select, Tooltip, Typography } from "antd";
import type { SubjectEntity } from "../../Processing/types";
import { ControllerBindingRow } from "./ControllerBindingRow";
import { useControllers } from "../../Contexts/dashboardHooks";
import {
  boundController,
  positionSubjectsForPlayers,
} from "../../Processing/controllerBindings";

const { Text } = Typography;
const { OptGroup, Option } = Select;

/**
 * The overflow tag on a narrow multi-select. The antd default renders "+ 3 ..." and then
 * ellipsizes even that; this one stays a short "+3" and names every hidden selection in a
 * tooltip on hover.
 */
function overflowTag(omitted: { label?: ReactNode; value?: unknown }[]) {
  const names = omitted
    .map((o) => (typeof o.label === "string" ? o.label : String(o.value ?? "")))
    .filter(Boolean)
    .join(", ");
  return (
    <Tooltip title={names}>
      <span>+{omitted.length}</span>
    </Tooltip>
  );
}

export interface PlayersFormFields {
  /** UUID of the Slicer this subject section follows, when it is bound. */
  subjectControllerId?: string;
  /** Follow the POSITIONS of that controller's athletes rather than the athletes. */
  subjectControllerMode?: "positions";
  selectedSubjects: string[];
  selectedDrills: string[];
  subjectActiveTab: "athletes" | "drills";
}

interface PlayersFormProps {
  value: PlayersFormFields;
  onChange: (patch: Partial<PlayersFormFields>) => void;
  getSubjectEntities: (subjectType: string) => SubjectEntity[];
  description?: string;
  hideDrills?: boolean;
  singleSelect?: boolean;
  playersOnly?: boolean;
  /** Show the "Dynamically controlled" row — the host widget's `dynamic.bindableSections`. */
  bindable?: boolean;
  /** Select size. Omit for antd's default (middle). */
  size?: "small" | "middle";
  /** Fires as either dropdown opens or closes. A closed dropdown means the pick is done. */
  onPopupOpenChange?: (open: boolean) => void;
}

export const PLAYERS_DEFAULTS: PlayersFormFields = {
  selectedSubjects: ["full-team"],
  selectedDrills: [],
  subjectActiveTab: "athletes",
};

function buildAthleteOptions(
  getSubjectEntities: (t: string) => SubjectEntity[],
) {
  const players = getSubjectEntities("player");
  const positions = getSubjectEntities("position");
  const groups = getSubjectEntities("group");
  return { players, positions, groups };
}

export function PlayersForm({
  value,
  onChange,
  getSubjectEntities,
  description,
  hideDrills = false,
  singleSelect = false,
  playersOnly = false,
  bindable = false,
  size,
  onPopupOpenChange,
}: PlayersFormProps) {
  const { players, positions, groups } = useMemo(
    () => buildAthleteOptions(getSubjectEntities),
    [getSubjectEntities],
  );

  const drills = useMemo(
    () => (hideDrills ? [] : getSubjectEntities("drill")),
    [getSubjectEntities, hideDrills],
  );

  const helpText =
    description ??
    (hideDrills
      ? "Choose which athletes or groups to include."
      : "Choose which athletes, groups, or drills to include.");

  // While bound, the selects show the CONTROLLER's subjects rather than this widget's own.
  // The widget's values stay untouched underneath and come back when the box is unchecked.
  const controllers = useControllers();
  const controller = bindable
    ? boundController(value.subjectControllerId, "subject", controllers)
    : undefined;
  const isBound = controller != null;
  const controllerMisc = controller?.spec.slicer_builder_misc;
  // In positions mode the athlete pills become the POSITIONS those athletes play — the
  // greyed selects must show what the widget will actually query, not the athletes it
  // derives from. Drills pass through either way, matching the engine.
  const controllerSubjects =
    controllerMisc?.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects;
  const shown: PlayersFormFields = isBound
    ? {
        ...value,
        selectedSubjects:
          value.subjectControllerMode === "positions"
            ? positionSubjectsForPlayers(controllerSubjects, getSubjectEntities)
            : controllerSubjects,
        selectedDrills: controllerMisc?.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
        subjectActiveTab: controllerMisc?.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
      }
    : value;

  return (
    <div className="awm-step-body">
      {bindable && (
        <ControllerBindingRow
          kind="subject"
          controllerId={value.subjectControllerId}
          mode={value.subjectControllerMode}
          onChange={(subjectControllerId, subjectControllerMode) =>
            onChange({ subjectControllerId, subjectControllerMode })}
        />
      )}

      <div className={`awm-form-stack${isBound ? " awm-bound-section" : ""}`}>
      {helpText && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {helpText}
        </Text>
      )}

      <div className="awm-labeled-row">
      {!hideDrills && (
        <Text strong className="awm-labeled-row-label">
          Athletes
        </Text>
      )}

      <Select
        {...(size && { size })}
        {...(!singleSelect && { mode: "multiple" as const })}
        placeholder="Full Team"
        value={singleSelect ? (shown.selectedSubjects[0] ?? undefined) : shown.selectedSubjects}
        onChange={(vals) => {
          let next = singleSelect ? (vals ? [vals as string] : []) : (vals as string[]);
          // Full Team cannot coexist with specific selections — resolve the conflict
          if (!singleSelect && next.includes("full-team") && next.length > 1) {
            if (value.selectedSubjects.includes("full-team")) {
              // Full team was already selected; user just added a specific → drop full team
              next = next.filter((v) => v !== "full-team");
            } else {
              // User just added full team while specifics were present → keep only full team
              next = ["full-team"];
            }
          }
          onChange({ selectedSubjects: next });
        }}
        onOpenChange={onPopupOpenChange}
        style={{ width: "100%" }}
        showSearch
        optionFilterProp="label"
        // Tags and the overflow tooltip must render the plain `label` string. The default
        // ("children") hands them the option's ReactNode — name + detail — which the
        // tooltip cannot join, so it degraded to raw `player-<id>` values.
        optionLabelProp="label"
        {...(!singleSelect && {
          maxTagCount: "responsive" as const,
          maxTagPlaceholder: overflowTag,
        })}
        allowClear
      >
        {!playersOnly && (
          <OptGroup label="Team">
            <Option value="full-team" label="Full Team">
              Full Team
            </Option>
          </OptGroup>
        )}
        {players.length > 0 && (
          <OptGroup label="Players">
            {players.map((p) => (
              <Option
                key={`player-${p.id}`}
                value={`player-${p.id}`}
                label={p.name}
              >
                {p.name}
                {p.detail && (
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                    {p.detail}
                  </Text>
                )}
              </Option>
            ))}
          </OptGroup>
        )}
        {!playersOnly && positions.length > 0 && (
          <OptGroup label="Positions">
            {positions.map((pos) => (
              <Option
                key={`pos-${pos.id}`}
                value={`pos-${pos.id}`}
                label={pos.name}
              >
                {pos.name}
              </Option>
            ))}
          </OptGroup>
        )}
        {!playersOnly && groups.length > 0 && (
          <OptGroup label="Groups">
            {groups.map((g) => (
              <Option
                key={`group-${g.id}`}
                value={`group-${g.id}`}
                label={g.name}
              >
                {g.name}
              </Option>
            ))}
          </OptGroup>
        )}
      </Select>
      </div>

      {!hideDrills && (
        <div className="awm-labeled-row">
          <Text strong className="awm-labeled-row-label">
            Drills
          </Text>
          <Select
            {...(size && { size })}
            {...(!singleSelect && { mode: "multiple" as const })}
            placeholder="Select drills..."
            value={singleSelect ? (shown.selectedDrills[0] ?? undefined) : shown.selectedDrills}
            onChange={(vals) => {
              const next = singleSelect ? (vals ? [vals as string] : []) : (vals as string[]);
              onChange({ selectedDrills: next });
            }}
            onOpenChange={onPopupOpenChange}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            {...(!singleSelect && {
              maxTagCount: "responsive" as const,
              maxTagPlaceholder: overflowTag,
            })}
            allowClear
          >
            {drills.map((d) => (
              <Option
                key={`drill-${d.id}`}
                value={`drill-${d.id}`}
                label={d.name}
              >
                {d.name}
              </Option>
            ))}
          </Select>
        </div>
      )}
      </div>
    </div>
  );
}

// Moved to Utils/subjectNames.ts so component-free callers can read it.
export { resolveSubjectNames } from "../../Utils/subjectNames";
